import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Megaphone, Sprout, RefreshCw, Lock, Loader2, Download, Eye, MousePointerClick } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PAGE_LABELS = { "/": "Hauptseite (/)", "/soytfb1126": "Facebook Ads (/soytfb1126)" };
const TARGET_LABELS = {
  "hero-cta": "I'M IN (Hero)",
  "pricing-cta": "I'M IN (Buchen)",
  "nicht-fuer-dich-cta": "I'M IN (Schluss)",
  "audio-taster-play": "Spielprobe · Play",
  "voice-prev": "Karussell · Zurück",
  "voice-next": "Karussell · Weiter",
};

const ease = [0.22, 1, 0.36, 1];
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay }}>
    {children}
  </motion.div>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, testid, delay = 0 }) => (
  <Reveal delay={delay}>
    <div className="rounded-xl border border-[#EBC479]/60 bg-[#FFFBEE] p-6 shadow-[0_18px_50px_-24px_rgba(92,29,16,0.35)]" data-testid={testid}>
      <div className="flex items-center gap-2.5 text-[#8A4A2E]">
        <Icon className="h-4 w-4" />
        <span className="font-heading text-xs font-semibold uppercase tracking-[0.18em]">{label}</span>
      </div>
      <p className="mt-3 font-heading text-4xl font-black text-[#5C1D10] sm:text-5xl">{value}</p>
    </div>
  </Reveal>
);

const SourceBadge = ({ source }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-heading text-[10px] font-bold uppercase tracking-wider ${
    source === "eigentlich-masterclass-fbads" ? "bg-[#5C1D10] text-[#FFF2B0]" : "border border-[#B26B3F]/50 text-[#8A4A2E]"
  }`}>
    {source === "eigentlich-masterclass-fbads" ? "FB Ads" : "Organisch"}
  </span>
);

export default function StatsPage() {
  const [key] = useState(() => new URLSearchParams(window.location.search).get("key") || "");
  const [data, setData] = useState(null);
  const [soyt, setSoyt] = useState(null);
  const [state, setState] = useState("loading"); // loading | denied | error | ready
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (isRefresh = false) => {
    if (!key) {
      setState("denied");
      return;
    }
    if (isRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API}/api/masterclass-signups/stats?key=${encodeURIComponent(key)}`);
      if (!res.ok) {
        setState("denied");
        return;
      }
      setData(await res.json());
      const soytRes = await fetch(`${API}/api/soyt-stats?key=${encodeURIComponent(key)}`);
      if (soytRes.ok) setSoyt(await soytRes.json());
      setLastUpdated(new Date());
      setState("ready");
    } catch {
      setState("error");
    } finally {
      setRefreshing(false);
    }
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FBEFC9] to-[#F4DFA6]" data-testid="stats-page">
      <div className="mx-auto max-w-4xl px-5 py-16 sm:py-20 lg:px-8">
        <Reveal>
          <p className="font-heading text-xs font-semibold uppercase tracking-[0.34em] text-[#8A4A2E]">LIMONATA Coaching</p>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <h1 className="font-heading text-4xl font-black leading-tight text-[#5C1D10] sm:text-5xl">Masterclass-Anmeldungen</h1>
            {state === "ready" && (
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => load(true)} data-testid="stats-refresh"
                  className="inline-flex items-center gap-2 rounded-sm border border-[#B26B3F]/50 px-5 py-2.5 font-heading text-xs font-bold uppercase tracking-wider text-[#5C1D10] transition-colors hover:bg-[#B26B3F]/10">
                  {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Aktualisieren
                </button>
                <a href={`${API}/api/masterclass-signups/export?key=${encodeURIComponent(key)}`} data-testid="stats-export"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#5C1D10] px-5 py-2.5 font-heading text-xs font-bold uppercase tracking-wider text-[#FFF2B0] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-14px_rgba(92,29,16,0.7)]">
                  <Download className="h-4 w-4" />
                  CSV Export
                </a>
              </div>
            )}
          </div>
          {lastUpdated && (
            <p className="mt-2 text-xs text-[#8A4A2E]" data-testid="stats-updated">Zuletzt aktualisiert: {formatDate(lastUpdated)} Uhr</p>
          )}
        </Reveal>

        {state === "loading" && (
          <div className="mt-20 flex items-center justify-center gap-3 text-[#8A4A2E]" data-testid="stats-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Lade Anmeldungen …
          </div>
        )}

        {(state === "denied" || state === "error") && (
          <Reveal delay={0.1}>
            <div className="mx-auto mt-16 max-w-md rounded-xl border border-[#EBC479]/60 bg-[#FFFBEE] p-8 text-center shadow-[0_18px_50px_-24px_rgba(92,29,16,0.35)]" data-testid="stats-access-denied">
              <Lock className="mx-auto h-8 w-8 text-[#B26B3F]" />
              <h2 className="mt-4 font-heading text-2xl font-bold text-[#5C1D10]">Kein Zugriff</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#5C1D10]/75">
                Diese Übersicht ist nur über Deinen persönlichen Link erreichbar. Bitte prüfe, ob der Link vollständig ist.
              </p>
            </div>
          </Reveal>
        )}

        {state === "ready" && data && (
          <>
            <Reveal delay={0.05}>
              <div className="mt-10" data-testid="soyt-tracking">
                <h2 className="font-heading text-lg font-bold uppercase tracking-[0.14em] text-[#5C1D10]">SOYT Seiten-Tracking</h2>
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                  <StatCard icon={Eye} label="Aufrufe Hauptseite (/)" value={soyt?.pageviews?.["/"] ?? 0} testid="soyt-pv-home" />
                  <StatCard icon={Eye} label="Aufrufe FB Ads (/soytfb1126)" value={soyt?.pageviews?.["/soytfb1126"] ?? 0} testid="soyt-pv-fb" delay={0.08} />
                </div>

                <div className="mt-6 overflow-hidden rounded-xl border border-[#EBC479]/60 bg-[#FFFBEE]">
                  <table className="w-full text-left text-sm" data-testid="soyt-clicks-table">
                    <thead>
                      <tr className="border-b border-[#EBC479]/60 font-heading text-[11px] font-bold uppercase tracking-wider text-[#8A4A2E]">
                        <th className="px-5 py-3">Button</th>
                        <th className="px-5 py-3">Seite</th>
                        <th className="px-5 py-3 text-right">Klicks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!soyt || soyt.clicks.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-5 py-6 text-sm text-[#8A4A2E]" data-testid="soyt-clicks-empty">
                            Noch keine Klicks gezählt. Sobald Besucherinnen die Buttons nutzen, erscheinen die Zahlen hier.
                          </td>
                        </tr>
                      ) : (
                        soyt.clicks.map((row, i) => (
                          <tr key={i} className="border-b border-[#EBC479]/40 last:border-0" data-testid={`soyt-click-row-${i}`}>
                            <td className="px-5 py-3.5 font-semibold text-[#5C1D10]">{TARGET_LABELS[row.target] || row.target}</td>
                            <td className="px-5 py-3.5 text-[#5C1D10]/75">{PAGE_LABELS[row.page] || row.page}</td>
                            <td className="px-5 py-3.5 text-right font-heading text-base font-black text-[#5C1D10]">{row.count}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {soyt && soyt.daily.length > 0 && (
                  <div className="mt-6 overflow-hidden rounded-xl border border-[#EBC479]/60 bg-[#FFFBEE]">
                    <table className="w-full text-left text-sm" data-testid="soyt-daily-table">
                      <thead>
                        <tr className="border-b border-[#EBC479]/60 font-heading text-[11px] font-bold uppercase tracking-wider text-[#8A4A2E]">
                          <th className="px-5 py-3">Tag</th>
                          <th className="px-5 py-3">Seite</th>
                          <th className="px-5 py-3 text-right">Aufrufe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soyt.daily.map((row, i) => (
                          <tr key={i} className="border-b border-[#EBC479]/40 last:border-0" data-testid={`soyt-daily-row-${i}`}>
                            <td className="px-5 py-3 text-[#5C1D10]/75">{row.day.split("-").reverse().join(".")}</td>
                            <td className="px-5 py-3 text-[#5C1D10]/75">{PAGE_LABELS[row.page] || row.page}</td>
                            <td className="px-5 py-3 text-right font-heading text-base font-black text-[#5C1D10]">{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Reveal>

            <div className="mt-10 grid gap-5 sm:grid-cols-3">
              <StatCard icon={Users} label="Gesamt" value={data.total} testid="stats-total" />
              <StatCard icon={Sprout} label="Organisch" value={data.organic} testid="stats-organic" delay={0.08} />
              <StatCard icon={Megaphone} label="FB Ads" value={data.fbads} testid="stats-fbads" delay={0.16} />
            </div>

            <Reveal delay={0.22}>
              <div className="mt-12" data-testid="stats-campaigns">
                <h2 className="font-heading text-lg font-bold uppercase tracking-[0.14em] text-[#5C1D10]">Anmeldungen pro Kampagne (FB Ads)</h2>
                {data.by_campaign.length === 0 ? (
                  <p className="mt-4 rounded-lg border border-dashed border-[#B26B3F]/40 px-5 py-6 text-sm text-[#8A4A2E]" data-testid="stats-campaigns-empty">
                    Noch keine Anmeldungen über Ads. Sobald Deine Kampagne läuft, siehst Du hier, welche Anzeige wie viele Anmeldungen gebracht hat.
                  </p>
                ) : (
                  <div className="mt-4 overflow-hidden rounded-xl border border-[#EBC479]/60 bg-[#FFFBEE]">
                    <table className="w-full text-left text-sm" data-testid="stats-campaign-table">
                      <thead>
                        <tr className="border-b border-[#EBC479]/60 font-heading text-[11px] font-bold uppercase tracking-wider text-[#8A4A2E]">
                          <th className="px-5 py-3">Kampagne</th>
                          <th className="px-5 py-3">Quelle</th>
                          <th className="px-5 py-3">Medium</th>
                          <th className="px-5 py-3 text-right">Anmeldungen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.by_campaign.map((row, i) => (
                          <tr key={i} className="border-b border-[#EBC479]/40 last:border-0" data-testid={`stats-campaign-row-${i}`}>
                            <td className="px-5 py-3.5 font-semibold text-[#5C1D10]">{row.campaign}</td>
                            <td className="px-5 py-3.5 text-[#5C1D10]/75">{row.utm_source}</td>
                            <td className="px-5 py-3.5 text-[#5C1D10]/75">{row.utm_medium}</td>
                            <td className="px-5 py-3.5 text-right font-heading text-base font-black text-[#5C1D10]">{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.28}>
              <div className="mt-12" data-testid="stats-recent">
                <h2 className="font-heading text-lg font-bold uppercase tracking-[0.14em] text-[#5C1D10]">Alle Anmeldungen</h2>
                {data.signups.length === 0 ? (
                  <p className="mt-4 rounded-lg border border-dashed border-[#B26B3F]/40 px-5 py-6 text-sm text-[#8A4A2E]">Noch keine Anmeldungen.</p>
                ) : (
                  <ul className="mt-4 divide-y divide-[#EBC479]/50 overflow-hidden rounded-xl border border-[#EBC479]/60 bg-[#FFFBEE]" data-testid="stats-recent-list">
                    {data.signups.map((row, i) => (
                      <li key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3.5" data-testid={`stats-recent-row-${i}`}>
                        <span className="font-semibold text-[#5C1D10]">{row.first_name}</span>
                        <span className="text-sm text-[#5C1D10]/60">{row.email}</span>
                        <SourceBadge source={row.source} />
                        {row.utms?.utm_campaign && (
                          <span className="text-xs text-[#8A4A2E]">Kampagne: {row.utms.utm_campaign}</span>
                        )}
                        <span className="ml-auto text-xs text-[#8A4A2E]/80">{formatDate(row.created_at)} Uhr</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          </>
        )}
      </div>
    </div>
  );
}
